import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { VenueDetail } from "../../../mocks/allVenues";

interface Props {
  venue: VenueDetail;
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <div key={s} className="w-3 h-3 flex items-center justify-center">
          <i className={`ri-star-fill text-xs ${s <= Math.floor(rating) ? "text-amber-400" : "text-stone-200"}`} />
        </div>
      ))}
    </div>
  );
}

export default function VenueCard({ venue }: Props) {
  const { t } = useTranslation();

  return (
    <div className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {/* Image */}
      <div className="relative overflow-hidden h-48 shrink-0">
        <img
          src={venue.image}
          alt={venue.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {/* Top badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {venue.isSponsored && (
            <span className="px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold font-body">
              {t("common_sponsored")}
            </span>
          )}
          {venue.isEditorPick && !venue.isSponsored && (
            <span className="px-2.5 py-1 rounded-full bg-forest-600 text-white text-xs font-semibold font-body">
              {t("common_editor_pick")}
            </span>
          )}
        </div>
        {/* Open/Closed */}
        <div className="absolute top-3 right-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium font-body ${
            venue.isOpen ? "bg-white/90 text-forest-700" : "bg-white/90 text-stone-500"
          }`}>
            <i className={`${venue.isOpen ? "ri-time-line" : "ri-time-line"} mr-1`} />
            {venue.isOpen ? t("common_open") : t("common_closed")}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Category + district */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-semibold text-forest-700 bg-forest-50 px-2 py-0.5 rounded-full font-body">
            {venue.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-stone-400 font-body">
            <i className="ri-map-pin-line text-xs" />
            {venue.district}
          </span>
          <span className="text-xs text-stone-400 font-body ml-auto">
            {venue.distanceKm} {t("cat_page_km")}
          </span>
        </div>

        {/* Name */}
        <h3 className="font-display font-bold text-anthracite-900 text-base mb-1.5 group-hover:text-forest-700 transition-colors leading-snug">
          {venue.name}
        </h3>

        {/* Description */}
        <p className="text-anthracite-400 text-xs font-body leading-relaxed mb-3 line-clamp-2 flex-1">
          {venue.description}
        </p>

        {/* Tags */}
        {venue.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {venue.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 text-xs font-body"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-3">
          <StarRow rating={venue.rating} />
          <span className="text-xs font-bold text-anthracite-800">{venue.rating}</span>
          <span className="text-xs text-stone-400 font-body">
            ({venue.reviews.toLocaleString()} {t("cat_page_reviews")})
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-stone-100 mt-auto">
          <Link
            to={`/venue/${venue.id}`}
            className="flex-1 py-2 text-xs font-semibold text-forest-700 border border-forest-200 rounded-xl hover:bg-forest-50 transition-colors cursor-pointer text-center whitespace-nowrap"
          >
            {t("cat_page_view_detail")}
          </Link>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(venue.name + " " + venue.address)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex-1 py-2 text-xs font-semibold text-white bg-forest-600 rounded-xl hover:bg-forest-500 transition-colors cursor-pointer text-center whitespace-nowrap"
          >
            {t("cat_page_directions")}
          </a>
        </div>
      </div>
    </div>
  );
}
